import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, X, Users, Phone, Mail, MessageSquare, Edit2, Trash2 } from 'lucide-react';

const STATUS_PIPELINE = ['novo', 'contato', 'visita', 'proposta', 'fechado', 'perdido'];
const STATUS_LABELS = {
  novo: 'Novo', contato: 'Contato', visita: 'Visita',
  proposta: 'Proposta', fechado: 'Fechado', perdido: 'Perdido',
};
const STATUS_COLORS = {
  novo: 'badge-novo', contato: 'badge-contato', visita: 'badge-visita',
  proposta: 'badge-proposta', fechado: 'badge-fechado', perdido: 'badge-perdido',
};
const ORIGENS = ['site', 'indicacao', 'portal', 'whatsapp', 'outro'];
const ORIGEM_LABELS = { site: 'Site', indicacao: 'Indicação', portal: 'Portal', whatsapp: 'WhatsApp', outro: 'Outro' };

const EMPTY_FORM = {
  nome: '', email: '', telefone: '', imovel_interesse: '',
  status: 'novo', origem: 'outro', observacoes: '',
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('table'); // 'table' | 'kanban'

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/leads');
      setLeads(data);
    } catch {
      toast.error('Erro ao carregar leads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const openNew = () => { setEditing(null); setForm(EMPTY_FORM); setShowModal(true); };
  const openEdit = (l) => { setEditing(l); setForm({ ...EMPTY_FORM, ...l }); setShowModal(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/leads/${editing.id}`, form);
        toast.success('Lead atualizado!');
      } else {
        await api.post('/leads', form);
        toast.success('Lead criado!');
      }
      setShowModal(false);
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar lead');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este lead?')) return;
    try {
      await api.delete(`/leads/${id}`);
      toast.success('Lead excluído');
      setLeads((l) => l.filter((x) => x.id !== id));
    } catch {
      toast.error('Erro ao excluir');
    }
  };

  const handleStatusChange = async (lead, newStatus) => {
    try {
      await api.put(`/leads/${lead.id}`, { ...lead, status: newStatus });
      setLeads((prev) => prev.map((l) => l.id === lead.id ? { ...l, status: newStatus } : l));
      toast.success(`Status: ${STATUS_LABELS[newStatus]}`);
    } catch {
      toast.error('Erro ao atualizar status');
    }
  };

  const filtered = leads.filter((l) => {
    if (filterStatus && l.status !== filterStatus) return false;
    if (search) {
      const s = search.toLowerCase();
      return (l.nome || l.name || '').toLowerCase().includes(s) ||
        (l.email || '').toLowerCase().includes(s) ||
        (l.telefone || '').toLowerCase().includes(s);
    }
    return true;
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content p-6 lg:p-8 pt-16 lg:pt-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Leads
            </h1>
            <p className="text-stone-500 text-sm mt-0.5">{leads.length} leads no total</p>
          </div>
          <div className="flex gap-2">
            <div className="flex border border-stone-200 rounded-lg overflow-hidden">
              {['table', 'kanban'].map((v) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors ${view === v ? 'bg-[#064E3B] text-white' : 'bg-white text-stone-500 hover:bg-stone-50'}`}
                >
                  {v === 'table' ? 'Tabela' : 'Kanban'}
                </button>
              ))}
            </div>
            <Button data-testid="btn-novo-lead" onClick={openNew} className="bg-[#064E3B] hover:bg-[#022C22] text-white gap-2">
              <Plus size={16} /> Novo Lead
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              placeholder="Buscar por nome, e-mail, telefone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9 border-stone-200 text-sm"
            />
          </div>
          <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40 h-9 border-stone-200 text-sm">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {STATUS_PIPELINE.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="text-center py-16 text-stone-400">Carregando leads...</div>
        ) : view === 'table' ? (
          /* Table View */
          <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
            {filtered.length === 0 ? (
              <div className="text-center py-16">
                <Users size={40} className="mx-auto text-stone-200 mb-3" />
                <p className="text-stone-500 text-sm">Nenhum lead encontrado</p>
                <Button onClick={openNew} className="mt-4 bg-[#064E3B] text-white gap-2"><Plus size={14} />Novo lead</Button>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    {['Nome', 'Contato', 'Status', 'Origem', 'Ações'].map((h) => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-bold text-stone-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-50">
                  {filtered.map((l) => (
                    <tr key={l.id} className="hover:bg-stone-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center shrink-0">
                            <span className="text-xs font-bold text-stone-500">{(l.nome || l.name || 'L').charAt(0).toUpperCase()}</span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-stone-800">{l.nome || l.name}</p>
                            {l.observacoes && <p className="text-xs text-stone-400 line-clamp-1 max-w-[200px]">{l.observacoes}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-0.5">
                          {l.email && <div className="flex items-center gap-1.5 text-xs text-stone-500"><Mail size={11} />{l.email}</div>}
                          {l.telefone && <div className="flex items-center gap-1.5 text-xs text-stone-500"><Phone size={11} />{l.telefone}</div>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Select value={l.status} onValueChange={(v) => handleStatusChange(l, v)}>
                          <SelectTrigger className={`h-7 w-28 text-xs font-semibold border-0 px-2 ${STATUS_COLORS[l.status] || ''}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_PIPELINE.map((s) => <SelectItem key={s} value={s} className="text-xs">{STATUS_LABELS[s]}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-stone-500">{ORIGEM_LABELS[l.origem] || l.origem}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <button onClick={() => openEdit(l)} className="p-1.5 hover:bg-stone-100 rounded-md transition-colors">
                            <Edit2 size={13} className="text-stone-500" />
                          </button>
                          <button onClick={() => handleDelete(l.id)} className="p-1.5 hover:bg-rose-50 rounded-md transition-colors">
                            <Trash2 size={13} className="text-rose-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          /* Kanban View */
          <div className="flex gap-4 overflow-x-auto pb-4">
            {STATUS_PIPELINE.filter((s) => s !== 'perdido').concat(['perdido']).map((status) => {
              const col = leads.filter((l) => l.status === status);
              return (
                <div key={status} className="shrink-0 w-64">
                  <div className="flex items-center justify-between mb-2 px-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${STATUS_COLORS[status]}`}>{STATUS_LABELS[status]}</span>
                    </div>
                    <span className="text-xs text-stone-400 font-medium">{col.length}</span>
                  </div>
                  <div className="bg-stone-50 rounded-lg p-2 space-y-2 min-h-[200px]">
                    {col.map((l) => (
                      <div key={l.id} className="bg-white border border-stone-200 rounded-md p-3 shadow-sm">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-medium text-stone-800 leading-tight">{l.nome || l.name}</p>
                          <div className="flex gap-0.5 shrink-0">
                            <button onClick={() => openEdit(l)} className="p-1 hover:bg-stone-100 rounded"><Edit2 size={11} className="text-stone-400" /></button>
                            <button onClick={() => handleDelete(l.id)} className="p-1 hover:bg-rose-50 rounded"><Trash2 size={11} className="text-rose-400" /></button>
                          </div>
                        </div>
                        {l.email && <p className="text-xs text-stone-400 mt-1 flex items-center gap-1"><Mail size={10} />{l.email}</p>}
                        {l.telefone && <p className="text-xs text-stone-400 mt-0.5 flex items-center gap-1"><Phone size={10} />{l.telefone}</p>}
                        <div className="flex gap-1 mt-2 flex-wrap">
                          {STATUS_PIPELINE.filter((s) => s !== status).map((s) => (
                            <button
                              key={s}
                              onClick={() => handleStatusChange(l, s)}
                              className="text-[10px] text-stone-400 hover:text-[#064E3B] font-medium"
                            >
                              → {STATUS_LABELS[s]}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
                <h2 className="font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {editing ? 'Editar Lead' : 'Novo Lead'}
                </h2>
                <button onClick={() => setShowModal(false)} className="text-stone-400 hover:text-stone-600"><X size={20} /></button>
              </div>
              <form onSubmit={handleSave} className="p-6 space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold text-stone-700">Nome *</Label>
                  <Input placeholder="Nome completo" value={form.nome} onChange={set('nome')} required className="border-stone-200" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-semibold text-stone-700">E-mail</Label>
                    <Input type="email" placeholder="email@exemplo.com" value={form.email} onChange={set('email')} className="border-stone-200" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-semibold text-stone-700">Telefone</Label>
                    <Input placeholder="+55 48 99999-0000" value={form.telefone} onChange={set('telefone')} className="border-stone-200" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-semibold text-stone-700">Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                      <SelectTrigger className="border-stone-200"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUS_PIPELINE.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-semibold text-stone-700">Origem</Label>
                    <Select value={form.origem} onValueChange={(v) => setForm((f) => ({ ...f, origem: v }))}>
                      <SelectTrigger className="border-stone-200"><SelectValue /></SelectTrigger>
                      <SelectContent>{ORIGENS.map((o) => <SelectItem key={o} value={o}>{ORIGEM_LABELS[o]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold text-stone-700">Observações</Label>
                  <textarea
                    placeholder="Interesses, preferências, notas..."
                    value={form.observacoes}
                    onChange={(e) => setForm((f) => ({ ...f, observacoes: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#064E3B]/20 focus:border-[#064E3B] resize-none"
                  />
                </div>
                <div className="flex gap-3 pt-1">
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1 border-stone-200">Cancelar</Button>
                  <Button type="submit" disabled={saving} className="flex-1 bg-[#064E3B] hover:bg-[#022C22] text-white">
                    {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : editing ? 'Salvar' : 'Criar'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
